import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetPassword } from '../lib/firebase';
import '../App.css';
import '../CSS/Header.css';
import logo from '../assets/logo-rof.svg';
import ProUpgradeModal from './ProUpgradeModal';
import ComingSoonModal from './ComingSoonModal';
import FEMAReportModal from './FEMAReportModal';
import FEMAReportToast from './FEMAReportToast';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../AuthContext';
import apiClient from '../lib/api-client';
import useFEMAReport from '../hooks/useFEMAReport';
import ProfileModal from './ProfileModal';



const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGuest, userData, checkingAuth, signOut, userTier } = useAuth();
  const menuContainerRef = useRef(null);

  const [showProModal, setShowProModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  // Mobile hamburger menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const runMobileAction = useCallback((action) => {
    try {
      action();
    } finally {
      closeMobileMenu();
    }
  }, [closeMobileMenu]);
  const {
    isGenerating,
    toastVisible,
    toastType,
    blobUrl,
    filename,
    progress,
    generate,
    generateMultiple,
    dismissToast,
  } = useFEMAReport();

  const handleReportButtonClick = useCallback(() => {
    setShowReportModal(true);
    closeMobileMenu();
  }, [closeMobileMenu]);

  const openProfileModal = () => {
    setShowProfileModal(true);
  };
  const { theme, toggleTheme } = useTheme();

  // Usage counter for signup tier
  const [usageDetails, setUsageDetails] = useState(null);

  useEffect(() => {
    if (userTier !== 'signup') return;

    const fetchUsage = async () => {
      try {
        const details = await apiClient.getUsageDetails();
        setUsageDetails(details);
      } catch (e) {
        console.error('Failed to fetch usage details', e);
      }
    };

    fetchUsage();

    // Re-fetch when a state drill-down completes (event dispatched by Home.jsx)
    window.addEventListener('usageUpdated', fetchUsage);
    return () => window.removeEventListener('usageUpdated', fetchUsage);
  }, [userTier]);

  // Auto-close hamburger menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (menuContainerRef.current && !menuContainerRef.current.contains(event.target)) {
        closeMobileMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  const handleForgotPassword = () => {
    setResetEmail(userData?.email || '');
    setResetMessage({ type: '', text: '' });
    setShowResetModal(true);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetMessage({ type: 'danger', text: 'Please enter your email address' });
      return;
    }
    setIsResetting(true);
    setResetMessage({ type: '', text: '' });
    try {
      await resetPassword(resetEmail);
      setResetMessage({ type: 'success', text: 'Password reset email sent. Please check your inbox and spam folder.' });
      setTimeout(() => {
        setShowResetModal(false);
        setResetMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMsg = 'Failed to send reset email. Please try again.';
      if (error.code === 'auth/user-not-found') errorMsg = 'No account found with this email address.';
      setResetMessage({ type: 'danger', text: errorMsg });
    } finally {
      setIsResetting(false);
    }
  };



  const handleLogout = async () => {
    setLogoutError('');
    setIsLoggingOut(true);
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
      setLogoutError('Logout failed. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className='col-12 header-shell' ref={menuContainerRef}>
        <div className="header d-flex justify-content-between align-items-center px-4 py-3 shadow-lg rounded-lg">
          <div className="d-flex align-items-center gap-4">
            <div className="d-flex align-items-center">
              <img src={logo} alt="Logo" className="me-3" style={{ height: '40px' }} />
            </div>
          </div>
          <div>
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="text-muted small">Verifying your account...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>


      <div className={`col-12 header-shell ${isMobileMenuOpen ? 'is-mobile-menu-open' : ''}`} ref={menuContainerRef}>
          <div className={`header d-flex justify-content-between align-items-center px-4 py-3 shadow-lg rounded-lg ${isMobileMenuOpen ? 'is-mobile-menu-open' : ''}`}>            {/* Left Section with Logo + Hamburger (mobile) */}
            <div className="d-flex align-items-center justify-content-between w-100">
              <div className="d-flex align-items-center">
                <img src={logo} alt="Logo" className="header-logo" style={{ height: '40px' }} />
              </div>

              <div className="header-trailing d-flex align-items-center gap-2">
                {isGuest && (
                  <Button
                    variant="outline-primary"
                    className="header-mobile-signin px-3 py-1"
                    onClick={() => { navigate('/auth'); closeMobileMenu(); }}
                    title="Sign In"
                  >
                    <i className="fas fa-sign-in-alt me-2"></i>Sign In
                  </Button>
                )}

                {/* Hamburger button — visible only on mobile, sits right of logo */}
                <button
                  type="button"
                  className={`hamburger-btn ${isMobileMenuOpen ? 'is-open' : ''}`}
                  aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-menu-drawer"
                  onClick={() => setIsMobileMenuOpen(prev => !prev)}
                >
                  <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
                </button>
              </div>

              {/* Right Section — hidden on mobile, shown via drawer */}
              <div className="header-actions d-flex align-items-center gap-3">

              {/* Upgrade to Pro + usage counter — signup tier only */}
              {userTier === 'signup' && (
                <div className="d-flex align-items-center gap-2">
                  {usageDetails !== null && (
                    <span
                      className="badge rounded-pill px-3 py-2"
                      style={{
                        backgroundColor: 'var(--bg-hover)',
                        color: usageDetails.daily_request_count >= usageDetails.daily_predictions_limit
                          ? '#ef4444'
                          : 'var(--text-main)',
                        border: '1px solid var(--border-color)',
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap'
                      }}
                      title="State predictions remaining today"
                    >
                      <i className="fas fa-map-marked-alt me-1"></i>
                      State predictions left : {Math.max(0, usageDetails.daily_predictions_limit - usageDetails.daily_request_count)}
                    </span>
                  )}
                  <Button
                    variant="warning"
                    className="fw-semibold px-3 py-1"
                    onClick={() => { setShowProModal(true); closeMobileMenu(); }}
                  >
                    <i className="fas fa-crown me-2"></i>Upgrade to Pro
                  </Button>
                </div>
              )}

              <OverlayTrigger placement="bottom" overlay={<Tooltip>Coming Soon</Tooltip>}>
                <Button
                  variant="outline-primary"
                  className="px-3 py-1 fw-semibold"
                  aria-label="What's Coming Soon"
                  onClick={() => { setShowComingSoonModal(true); closeMobileMenu(); }}
                >
                  <i className="fas fa-rocket me-2"></i>Coming Soon
                </Button>
              </OverlayTrigger>

              {(userTier === 'pro' || userTier === 'signup') && !checkingAuth && (
                <Button
                  variant="outline-primary"
                  className="px-3 py-1 fw-semibold"
                  aria-label="Download FEMA Report"
                  onClick={handleReportButtonClick}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <span
                      className="badge rounded-pill px-2 py-1"
                      style={{
                        backgroundColor: 'var(--bg-hover)',
                        color: 'var(--text-main)',
                        border: '1px solid var(--border-color)',
                        fontSize: '0.8rem',
                      }}
                      aria-label="Generating report"
                    >
                      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    </span>
                  ) : (
                    <i className="fas fa-file-download me-2" />
                  )}
                  FEMA Report
                </Button>
              )}

              {isGuest ? (
                /* Guest: Sign In + Sign Up buttons */
                <>
                  <Button
                    variant="outline-primary"
                    className="px-3 py-1"
                    onClick={() => { navigate('/auth'); closeMobileMenu(); }}
                    title="Sign In"
                  >
                    <i className="fas fa-sign-in-alt me-2"></i>Sign In
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="px-3 py-1"
                    onClick={() => { navigate('/auth?signup=true'); closeMobileMenu(); }}
                    title="Create Account"
                  >
                    <i className="fas fa-user-plus me-2"></i>Sign Up
                  </Button>
                </>
              ) : (
                /* Authenticated: Profile + Logout */
                <>
                  <Button
                    variant="light"
                    className="p-2"
                    title="Profile"
                    aria-label="Open profile"
                    onClick={() => { openProfileModal(); closeMobileMenu(); }}
                  >
                    <i className="fas fa-user text-primary fs-5" aria-hidden="true"></i>
                  </Button>

                  <Button
                    variant="light"
                    onClick={() => { handleLogout(); closeMobileMenu(); }}
                    className="p-2"
                    title="Logout"
                    aria-label="Log out"
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="fas fa-sign-out-alt text-primary fs-5" aria-hidden="true"></i>
                    )}
                  </Button>
                </>
              )}

              <OverlayTrigger placement="bottom" overlay={<Tooltip>Important Disclaimer</Tooltip>}>
                <Button variant="light" className="p-2" aria-label="Open important disclaimer" onClick={() => { setShowDisclaimerModal(true); closeMobileMenu(); }}>
                  <i className="fas fa-info-circle text-primary fs-5"></i>
                </Button>
              </OverlayTrigger>

              <Button
                variant="light"
                className="p-2"
                onClick={toggleTheme}
                title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} text-primary fs-5`} aria-hidden="true"></i>
              </Button>
            </div>
            </div>
          </div>

          {/* Mobile drawer — rendered below the header bar when open */}
          {isMobileMenuOpen && (
            <div className="mobile-menu-drawer" id="mobile-menu-drawer" role="navigation" aria-label="Mobile navigation menu">

              {/* Upgrade to Pro + usage counter — signup tier only */}
              {userTier === 'signup' && (
                <div className="mobile-menu-section">
                  {usageDetails !== null && (
                    <span
                      className="mobile-usage-badge"
                      style={{
                        backgroundColor: 'var(--bg-hover)',
                        color: usageDetails.daily_request_count >= usageDetails.daily_predictions_limit
                          ? '#ef4444'
                          : 'var(--text-main)',
                        border: '1px solid var(--border-color)',
                        fontSize: '0.9rem',
                      }}
                      title="State predictions remaining today"
                    >
                      <i className="fas fa-map-marked-alt me-1"></i>
                      State predictions left: {Math.max(0, usageDetails.daily_predictions_limit - usageDetails.daily_request_count)}
                    </span>
                  )}
                  <Button
                    variant="warning"
                    className="fw-semibold w-100 mobile-menu-action"
                    onClick={() => runMobileAction(() => setShowProModal(true))}
                  >
                    <i className="fas fa-crown me-2"></i>Upgrade to Pro
                  </Button>
                </div>
              )}

              {isGuest ? (
                <div className="mobile-menu-section">
                  <span className="mobile-menu-section-title">Account</span>
                  <Button
                    variant="primary"
                    className="w-100 mobile-menu-action"
                    onClick={() => runMobileAction(() => navigate('/auth?signup=true'))}
                  >
                    <i className="fas fa-user-plus me-2"></i>Sign Up
                  </Button>
                </div>
              ) : (
                <div className="mobile-menu-section">
                  <span className="mobile-menu-section-title">Account</span>
                  <Button
                    variant="outline-secondary"
                    className="w-100 mobile-menu-action"
                    onClick={() => runMobileAction(openProfileModal)}
                  >
                    <i className="fas fa-user me-2"></i>Profile
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="w-100 mobile-menu-action"
                    onClick={() => runMobileAction(handleLogout)}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="fas fa-sign-out-alt me-2"></i>
                    )}
                    Logout
                  </Button>
                </div>
              )}

              <div className="mobile-menu-section">
                <span className="mobile-menu-section-title">Tools</span>
                <div className="mobile-menu-grid">
                  <Button
                    variant="outline-primary"
                    className="mobile-menu-action"
                    onClick={() => runMobileAction(() => setShowComingSoonModal(true))}
                  >
                    <i className="fas fa-rocket me-2"></i>Coming Soon
                  </Button>
                  {(userTier === 'pro' || userTier === 'signup') && !checkingAuth && (
                    <Button
                      variant="outline-primary"
                      className="mobile-menu-action"
                      aria-label="Download FEMA Report"
                      onClick={handleReportButtonClick}
                      disabled={isGenerating}
                    >
                      {isGenerating ? (
                        <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                      ) : (
                        <i className="fas fa-file-download me-2" />
                      )}
                      FEMA Report
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    className="mobile-menu-action"
                    onClick={() => runMobileAction(() => setShowDisclaimerModal(true))}
                  >
                    <i className="fas fa-info-circle me-2"></i>Disclaimer
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="mobile-menu-action"
                    onClick={() => runMobileAction(toggleTheme)}
                  >
                    <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} me-2`}></i>
                    {theme === 'dark' ? 'Light' : 'Dark'} Mode
                  </Button>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Disclaimer Modal */}
      <Modal className="header-modal" show={showDisclaimerModal} onHide={() => setShowDisclaimerModal(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning me-2"></i>
            Important Disclaimer
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="p-2">
            <h6 className='fw-bold'>Disclaimer</h6>
            <p className="mb-3">
              The accuracy and reliability of the results generated by this platform are inherently
              dependent on the quality, completeness, and granularity of the input data provided.
              All models used within the platform are pre-trained and may not reflect real-time
              system dynamics, region-specific operational variables, or emergent grid conditions.
            </p>
            <p>Outage duration predictions are based solely on historical data and past outage patterns. Actual restoration times may vary due to crew availability and performance. As such, these estimates may vary from actual restoration times.</p>
            <hr className="my-3" />
            <p className="mb-3">
              Use of this platform constitutes acceptance of these limitations.
              For mission-critical operations, independent verification is recommended.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <Button variant="outline-secondary" onClick={() => setShowDisclaimerModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ProUpgradeModal show={showProModal} onHide={() => setShowProModal(false)} />
      <ComingSoonModal show={showComingSoonModal} onHide={() => setShowComingSoonModal(false)} />
      <FEMAReportModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        onGenerate={(region, scenario) => {
          setShowReportModal(false);
          generate(region, scenario);
        }}
        onGenerateMultiple={(regionIds, scenario) => {
          setShowReportModal(false);
          generateMultiple(regionIds, scenario);
        }}
      />
      <FEMAReportToast
        show={toastVisible}
        type={toastType}
        blobUrl={blobUrl}
        filename={filename}
        progress={progress}
        onDismiss={dismissToast}
      />

      <Modal className="header-modal" show={Boolean(logoutError)} onHide={() => setLogoutError('')} centered>
        <Modal.Header closeButton>
          <Modal.Title>Logout Failed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{logoutError}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLogoutError('')}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Password Reset Modal */}
      <Modal className="header-modal" show={showResetModal} onHide={() => setShowResetModal(false)} centered backdrop="static" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Enter your email address and we'll send you instructions to reset your password.</p>
          {resetMessage.text && <div className={`alert alert-${resetMessage.type}`}>{resetMessage.text}</div>}
          <Form onSubmit={handlePasswordReset}>
            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </Form.Group>
            <div className="d-grid gap-2">
              <Button variant="primary" type="submit" disabled={isResetting}>
                {isResetting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowResetModal(false)} disabled={isResetting}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Profile Modal */}
      <ProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        onResetPassword={handleForgotPassword}
        isResetting={isResetting}
      />


    </>
  );
};

// FeatureItem component (unchanged)
const FeatureItem = ({ available = false, title, items = [] }) => {
  return (
    <div className="feature-item d-flex mb-3">
      <div className={`feature-icon ${available ? 'pro-available' : 'free-unavailable'} me-3`}>
        <i className={`fas ${available ? 'fa-check-circle text-success' : 'fa-times-circle text-muted'}`}></i>
      </div>
      <div>
        <h6 className={`${available ? '' : 'text-muted'} mb-1`}>{title}</h6>
        {available ? (
          <ul className="text-secondary small ps-3 mb-0">
            {items.map((item, idx) => (<li key={idx}>{item}</li>))}
          </ul>
        ) : (
          <p className="text-muted small mb-0">Not available in Free Tier</p>
        )}
      </div>
    </div>
  );
};

export default Header;